"use strict";

define('rs/services/study.service',['./abstract.service', './document.service', 'rs/models/study', 'rs/models/template', 'rs/models/document', 'rs/models/assignment.builder', 'rs/models/assignments', 'rs/models/role', 'rs/models/person', 'rs/models/task', 'rs/models/document.decision', 'rs/models/pdf', 'rs/models/study.proposal', 'rs/models/product', 'evisions/validation'], function (AbstractService, DocumentService, Study, Template, Document, AssignmentBuilder, Assignments, Role, Person, Task, Decision, PDFGenerator, StudyProposal, Product, validation) {
  var StudyService = AbstractService.extend({
    properties: ['studyId'],
    studyValidation: {
      title: [validation.empty('You must enter a study title'), validation.checkLength(3, 600)]
    },
    documentService: function documentService(docId) {
      var documentService = new DocumentService({
        documentId: docId
      });
      this.listenTo(documentService, 'workflow:progress', function () {
        this.update('study', 'documents', 'templates', 'assignments');
      });
      this.listenTo(documentService, 'delete', function () {
        this.stopListening(documentService);
        this.update('documents', 'templates', 'assignments', 'study');
      });
      return documentService;
    },
    getStudy: function getStudy(force) {
      return this.cache('study', this.requestStudy, force);
    },
    getDocuments: function getDocuments(force) {
      return this.cache('documents', this.requestDocuments, force);
    },
    getOpenDocuments: function getOpenDocuments() {
      return _.reject(this.getDocuments(), function (doc) {
        return doc.finalizedDate;
      });
    },
    getFinalizedDocuments: function getFinalizedDocuments() {
      return _.filter(this.getDocuments(), function (doc) {
        return doc.finalizedDate;
      });
    },
    getTemplates: function getTemplates(force) {
      return this.cache('templates', this.requestTemplates, force);
    },
    getAnalysts: function getAnalysts(force) {
      return this.cache('analysts', this.requestAnalysts, force);
    },
    getAssignments: function getAssignments(force) {
      return this.cache('assignments', this.requestAssignments, force);
    },
    getStudyProposals: function getStudyProposals(force) {
      return this.cache('studyProposals', this.requestStudyProposals, force);
    },
    getInitialDocument: function getInitialDocument(docId) {
      var doc = null;
      if (docId) {
        doc = this.findDocumentById(docId);
      }
      if (!doc) {
        doc = this.findLastModifiedDocument();
      }
      return doc;
    },
    findLastModifiedDocument: function findLastModifiedDocument() {
      return _.reduce(this.getDocuments(), function (greatest, doc) {
        if (greatest && greatest.activityDate > doc.activityDate) {
          return greatest;
        }
        return doc;
      }, null);
    },
    findDocumentById: function findDocumentById(docId) {
      return _.find(this.getDocuments(), function (d) {
        return d.id == docId;
      });
    },
    canLinkProposals: function canLinkProposals() {
      var assignments = this.getAssignments(),
        personId = this.getPerson().getId();
      return this.hasRole('IRB_ANALYST') || !_.isEmpty(assignments.getAssignmentsByPersonId(personId));
    },
    getOrganizationSummary: function getOrganizationSummary() {
      var orgs = this.getStudy().getOrganizations(),
        orgStr = '';
      if (orgs && orgs.length) {
        orgStr = _.pluck(orgs, 'title').join(', ');
      }
      return orgStr;
    },
    requestStudy: function requestStudy() {
      return Study.requestStudy(this.getStudyId());
    },
    requestDocuments: function requestDocuments() {
      return Document.requestDocuments(this.getStudyId());
    },
    requestApprovedDocuments: function requestApprovedDocuments() {
      return Document.requestApprovedDocuments(this.getStudyId());
    },
    requestTemplates: function requestTemplates() {
      return Template.requestPublishedTemplatesByStudyId(this.getStudyId());
    },
    requestAnalysts: function requestAnalysts() {
      return Role.requestRoles().bind(this).then(function (roles) {
        var analystRole = _.find(roles, function (role) {
          return role.getName() == 'ROLE_IRB_ANALYST';
        });
        return Person.requestPersonsByRole(analystRole.getId());
      });
    },
    requestAssignments: function requestAssignments(force) {
      var study = this.cache.get('study');
      if (study && !force) {
        return Promise.resolve(study).bind(this).then(function (study) {
          var assignments = study.getAssignments();
          return Assignments.requestAll({
            assignments: assignments,
            studyId: this.getStudyId(),
            lazyFetch: assignments != null
          });
        });
      }
      return Assignments.requestAll({
        studyId: this.getStudyId()
      });
    },
    requestStudyProposals: function requestStudyProposals() {
      return Promise.resolve(this.getAssignments()).bind(this).then(function () {
        return Product.findByName('SP');
      }).then(function (product) {
        if (!product) {
          return [];
        }
        var legacyPersonIds = _.chain(this.getResearchPeople()).pluck('legacyCayuseId').compact().value();
        return StudyProposal.requestAllByStudyId(this.getStudyId(), legacyPersonIds);
      });
    },
    getSortedTemplates: function getSortedTemplates() {
      return this.sortPropertyBy('templates', 'type');
    },
    getResearchPeople: function getResearchPeople() {
      var assignments = this.getAssignments();
      var researchers = [].concat(assignments.getPersonsByAssignmentType('PRINCIPAL_INVESTIGATOR')).concat(assignments.getPersonsByAssignmentType('PRIMARY_CONTACT')).concat(assignments.getPersonsByAssignmentType('IRB_CO_PRINCIPAL_INVESTIGATOR')).concat(assignments.getPersonsByAssignmentType('INVESTIGATOR'));
      return researchers;
    },
    createDocument: function createDocument(template) {
      var workflowService = null;
      return Document.requestCreateDocument({
        studyId: this.getStudyId(),
        templateId: template.getId(),
        templateRevisionNumber: template.getRevisionNumber()
      }).bind(this).tap(function (doc) {
        workflowService = this.documentService(doc.id).getWorkflowService();
        return Promise.join(workflowService.start(), this.getDocuments());
      }).tap(function (doc) {
        if (_.isEmpty(this.getDocuments()) && template.getType() != 'Legacy') {
          var ab = new AssignmentBuilder({
            document: doc
          });
          ab.addPerson(this.getPerson(), 'PRINCIPAL_INVESTIGATOR');
          return ab.requestApply();
        }
      }).tap(function (doc) {
        var count = 0,
          _loop = null;
        _loop = function loop(id) {
          return Promise.resolve(workflowService.getTasks()).then(function (tasks) {
            count++;
            if (tasks.length == 0 && count < 5) {
              return Promise.delay(200).then(function () {
                return _loop(id);
              });
            }
          });
        };
        return _loop(doc.id);
      }).tap(function () {
        this.trigger('create:document');
        return this.update('documents', 'templates', 'assignments', 'study');
      });
    },
    updateStudy: function updateStudy(properties) {
      return Promise.resolve(this.getStudy()).bind(this).tap(function () {
        return this.validate(properties, this.studyValidation);
      }).then(function () {
        return this.getStudy().requestUpdate(properties);
      });
    },
    deleteStudy: function deleteStudy() {
      return Study.requestDeleteStudy(this.getStudyId());
    }
  });
  return StudyService;
});

